/**
 * 待ち画面。 全画面で「まだ何も出せない」 ときの唯一の見せ方。
 *
 * これまでは `<main className="centered">読み込み中…</main>` の素の一文だった。
 * 和紙の地に本文サイズの文が 1 行浮いているだけで、 サイトの造形 (ロゴ / 罫 / 石)
 * と何もつながっておらず、 壊れて止まっているようにも見えていた。
 *
 * 置き換えたもの:
 *   - 盤の石と同じ gradient の石が、 碁盤の罫の上に左から順に置かれて消える。
 *     ここで動いているのは「盤」 だと一目で分かり、 かつ地味。
 *   - 文言は TitleBar と同じ mono + 広い字間の小さいラベルに落とす。
 *   - 起動時だけ mixed/games のロゴを出す (Home と同じ組み)。
 *
 * ちらつき対策: `delayMs` (既定 260ms) より早く終わる待ちでは **何も出さない**。
 * wasm も session もキャッシュが効けば 100ms 以下で終わるので、 素朴に出すと
 * 石が一瞬光って消えるだけのノイズになる。
 */

import { useEffect, useState } from 'react';

interface Props {
  /** 石の下に出す一文。 末尾の「…」 は CSS 側で付けるので書かない。 */
  label?: string;
  /** mixed/games のロゴを添える。 アプリ起動時の待ちだけ true。 */
  brand?: boolean;
  /** これより短い待ちでは何も描かない。 0 で即表示。 */
  delayMs?: number;
}

/** 罫の上に石が並ぶ待ちの印。 中身だけ (= 中央寄せは呼び出し側 / LoadingScreen)。 */
export function Loading({ label = '読み込み中', brand = false, delayMs = 260 }: Props) {
  const [shown, setShown] = useState(delayMs <= 0);
  useEffect(() => {
    if (delayMs <= 0) return;
    const t = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);

  // 待ちが短いときは場所だけ取って黙っている (aria-busy は出す)。
  if (!shown) return <div className="loading-wait" aria-busy="true" />;

  return (
    <div className="loading-wait loading-wait--shown" role="status" aria-live="polite">
      {brand && (
        <p className="loading-brand" aria-hidden>
          mixed<span className="loading-brand-slash">/</span>games
        </p>
      )}
      <div className="loading-stones" aria-hidden>
        <i className="loading-stone" />
        <i className="loading-stone" />
        <i className="loading-stone" />
        <i className="loading-stone" />
        <i className="loading-stone" />
      </div>
      <p className="loading-label">{label}</p>
    </div>
  );
}

/** 画面まるごとの待ち。 ルート切り替え / wasm 待ちはこれを返す。 */
export function LoadingScreen(props: Props) {
  return (
    <main className="centered">
      <Loading {...props} />
    </main>
  );
}
